import { ShaderProgram } from '../renderer/ShaderProgram';
import { Mesh } from '../renderer/Mesh';
import type { Camera } from '../renderer/Camera';
import type { Renderable } from '../renderer/RenderPipeline';

import vertSrc from '../shaders/starfield.vert.glsl?raw';
import fragSrc from '../shaders/starfield.frag.glsl?raw';

/**
 * Fullscreen procedural starfield rendered behind everything.
 * Works in screen-pixel space so stars stay sharp at any zoom.
 * Subtle parallax via camera offset; no zoom-scaling.
 */
export class StarfieldRenderer implements Renderable {
  private _shader: ShaderProgram;
  private _tri: Mesh;
  private _startTime = performance.now();

  constructor(gl: WebGL2RenderingContext) {
    this._shader = new ShaderProgram(gl, vertSrc, fragSrc);

    const verts = new Float32Array([
      -1, -1,
       3, -1,
      -1,  3,
    ]);

    this._tri = new Mesh(gl, this._shader, verts, [
      { name: 'a_position', size: 2 },
    ]);
  }

  render(gl: WebGL2RenderingContext, camera: Camera): void {
    gl.disable(gl.DEPTH_TEST);

    this._shader.use();
    this._shader.setVec2('u_resolution',
      gl.drawingBufferWidth, gl.drawingBufferHeight);
    this._shader.setVec2('u_offset', camera.target.x, camera.target.z);
    this._shader.setFloat('u_yaw', camera.yaw);
    this._shader.setFloat('u_pitch', camera.pitch);
    this._shader.setFloat('u_time',
      (performance.now() - this._startTime) * 0.001);
    this._tri.draw(gl.TRIANGLES);

    gl.enable(gl.DEPTH_TEST);
  }

  dispose(): void {
    this._shader.dispose();
    this._tri.dispose();
  }
}
